import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Order, OrderItem, Product, Profile, OrderHistory } from '@/types';
import { getOrder, updateOrder, removeOrderItem } from '@/api/orders';
import { supabase } from '@/lib/supabaseClient';
import { fixOrderPackedStatus } from '@/api/fixOrderStatus';
import { sendOrderStatusEmail } from '@/emails/unifiedEmailService';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useZxing } from 'react-zxing';
import { 
  ChevronLeft, 
  Loader2, 
  Plus, 
  Trash,
  AlertTriangle, 
  X, 
  CheckCircle2, 
  Clock, 
  Trash2, 
  Filter, 
  ShoppingCart,
  Camera,
  CameraOff,
  User,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { format } from 'date-fns';
import OrderHistoryTable from '@/components/OrderHistoryTable';
import SwedishDateDisplay from '@/components/SwedishDateDisplay';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface OrderEditorProps {
  orderId: string;
  onSave?: (orderId: string) => void;
  onCancel?: () => void;
}

// Extended interface for our local use to include delivery fields
interface ExtendedOrder extends Order {
  delivery_date?: string;
  delivery_time?: string;
  delivery_notes?: string;
  delivery_address?: string;
  display_id?: number;
}

const OrderEditor: React.FC<OrderEditorProps> = ({ orderId, onSave, onCancel }) => {
  const { t } = useTranslation();
  const supabaseClient = useSupabaseClient();
  const [order, setOrder] = useState<ExtendedOrder | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [itemQuantity, setItemQuantity] = useState<number>(1);
  const [originalStatus, setOriginalStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [lastScanResult, setLastScanResult] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);

  const { ref } = useZxing({
    onDecodeResult(result) {
      handleScanResult(result.getText());
    },
    onError(error: Error) {
      console.error(error);
      setScanError(error.message || 'Scanner error');
    },
  });

  // Handle barcode scan result
  const handleScanResult = async (scannedCode: string | undefined | null) => {
    if (!scannedCode || !order || loadingItemId) return; // Ignore empty scans or if already processing

    setScanError(null); // Clear previous errors
    setLastScanResult(scannedCode); // Store last scan
    console.log('Scanned barcode:', scannedCode);

    let matchingItem: OrderItem | undefined;
    let isWeighted = false;
    let parsedProductId: string | null = null;
    let priceInKronor: number | null = null;

    // --- Barcode Type Detection and Parsing ---
    if (scannedCode.startsWith('21') && scannedCode.length === 13) {
      isWeighted = true;
      try {
        parsedProductId = scannedCode.substring(2, 8); // Digits 3-8 (index 2 to 7)
        const priceInOreStr = scannedCode.substring(8, 13); // Digits 9-13 (index 8 to 12)
        const priceInOre = parseInt(priceInOreStr, 10);
        if (isNaN(priceInOre)) {
          throw new Error('Failed to parse price from barcode.');
        }
        priceInKronor = priceInOre / 100.0;

        // Find matching weighted item by barcode
        matchingItem = order.order_items.find(item => item.product.barcode === parsedProductId);

      } catch (error: any) {
        console.error("Error parsing weighted barcode:", error);
        setScanError(`Error parsing weighted code: ${error.message}`);
        return;
      }
    } else {
      // Find matching standard item - try both barcode and product ID
      matchingItem = order.order_items.find(item => 
        item.product.barcode === scannedCode || 
        item.product.id === scannedCode
      );
    }

    // --- Process Match ---
    if (!matchingItem) {
      setScanError(`Item with code "${scannedCode}" not found in this order.`);
      return;
    }

    // Check if already picked
    if (matchingItem.status === 'picked') {
      setScanError(`Item ${matchingItem.product.name} already picked.`);
      return;
    }

    setLoadingItemId(matchingItem.id); // Show loading indicator for this item

    try {
      // --- API Call ---
      const updateData: Partial<OrderItem> = {
        updated_at: new Date().toISOString(),
        status: 'picked' as const
      };

      if (isWeighted && priceInKronor !== null) {
        updateData.price_at_time = priceInKronor;
      }

      const { error: updateError } = await supabase
        .from('order_items')
        .update(updateData)
        .eq('id', matchingItem.id);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      const updatedOrderItems = order.order_items.map(item => {
        if (item.id === matchingItem!.id) {
          const updatedItem: OrderItem = {
            ...item,
            ...(isWeighted && priceInKronor !== null ? { price_at_time: priceInKronor } : {}),
            status: 'picked'
          };
          return updatedItem;
        }
        return item;
      });

      setOrder({ ...order, order_items: updatedOrderItems });

      // Show success message
      toast.success(`Item ${matchingItem.product.name} marked as picked${
        isWeighted ? ` (Price: ${priceInKronor?.toFixed(2)} kr)` : ''
      }`);

    } catch (error: any) {
      console.error("Error updating item:", error);
      setScanError(`Failed to update item ${matchingItem.product.name}: ${error.message}`);
      // Refresh order data to ensure consistency
      fetchOrder(order.id);
    } finally {
      setLoadingItemId(null); // Hide loading indicator
    }
  };

  // Calculate total from order items
  const calculateOrderTotal = (): number => {
    if (!order) return 0;
    return order.order_items.reduce((total: number, item: OrderItem) => {
      return total + (item.quantity * (item.price_at_time || item.product.price));
    }, 0);
  };

  // Calculate VAT amounts grouped by VAT rate
  const calculateVatAmounts = (): { [rate: number]: { vatAmount: number, totalAmount: number, baseAmount: number } } => {
    if (!order) return {};
    
    const vatByRate: { [rate: number]: { vatAmount: number, totalAmount: number, baseAmount: number } } = {};
    
    order.order_items.forEach((item: OrderItem) => {
      // Default to 12% if vat_rate is not defined
      const vatRate = (item.product as any).vat_rate || 12;
      const itemPrice = item.price_at_time || item.product.price;
      const itemTotal = item.quantity * itemPrice;
      const baseAmount = itemTotal / (1 + vatRate / 100);
      const vatAmount = itemTotal - baseAmount;
      
      if (!vatByRate[vatRate]) {
        vatByRate[vatRate] = { vatAmount: 0, totalAmount: 0, baseAmount: 0 };
      }
      
      vatByRate[vatRate].vatAmount += vatAmount;
      vatByRate[vatRate].totalAmount += itemTotal;
      vatByRate[vatRate].baseAmount += baseAmount;
    });
    
    return vatByRate;
  };

  // Fixing TypeScript errors by ensuring profile exists before updating
  const updateProfileField = (field: keyof Profile, value: any) => {
    if (!order || !order.profile) return;
    
    setOrder({
      ...order,
      profile: {
        ...order.profile,
        [field]: value
      }
    });
  };

  // Update address field safely handling object/string types
  const updateProfileAddress = (value: string) => {
    if (!order || !order.profile) return;
    
    let newAddress;
    if (typeof order.profile.address === 'object') {
      newAddress = { ...order.profile.address, street: value };
    } else {
      // Convert string to address object with street property
      newAddress = { street: value };
    }
    
    setOrder({
      ...order,
      profile: {
        ...order.profile,
        address: newAddress
      }
    });
  };

  // Parse address data to ensure proper display
  const parseAddressData = (addressData: any) => {
    if (!addressData) return { street: '', postal_code: '', city: '' };
    
    try {
      // If address is already an object, use it
      if (typeof addressData === 'object') {
        return {
          street: addressData.street || '',
          postal_code: addressData.postal_code || '',
          city: addressData.city || ''
        };
      }

      // If address is a string, try to parse it as JSON
      if (typeof addressData === 'string') {
        try {
          const parsed = JSON.parse(addressData);
          if (typeof parsed === 'object') {
            return {
              street: parsed.street || '',
              postal_code: parsed.postal_code || '',
              city: parsed.city || ''
            };
          }
        } catch (e) {
          // Not valid JSON, might be just a string address
          // Try to extract postal code if it's in the format: "Street, 12345 City"
          const postalCodeMatch = addressData.match(/,?\s*(\d{3}\s?\d{2})\s/);
          if (postalCodeMatch) {
            const postalCode = postalCodeMatch[1];
            const parts = addressData.split(postalCode);
            return {
              street: parts[0].replace(/,\s*$/, '').trim(),
              postal_code: postalCode,
              city: parts[1].trim()
            };
          }
        }
      }

      // Fallback: just return the address as street
      return {
        street: String(addressData),
        postal_code: '',
        city: ''
      };
    } catch (error) {
      console.error('Error parsing address data:', error);
      return {
        street: typeof addressData === 'string' ? addressData : '',
        postal_code: '',
        city: ''
      };
    }
  };

  // Fetch order data with all related items
  const fetchOrder = async (id: string, retryCount = 0, forceRefresh = false) => {
    try {
      setIsLoading(true);
      console.log(`Fetching order ${id}${forceRefresh ? ' (force refresh)' : ''}`);
      
      const data = await getOrder(id);
      
      if (!data) {
        throw new Error(`Order ${id} not found`);
      }
      
      console.log('Order data fetched successfully');
      console.log('Order customer info:', {
        profileInfo: data.profiles || data.profile,
        guestEmail: data.guest_email,
        guestDetails: data.guest_details,
        address: data.address
      });
      
      // Handle inconsistencies in order total
      const calculatedTotal = data.order_items.reduce((sum: number, item: any) => {
        return sum + (item.quantity * (item.price_at_time || item.product.price));
      }, 0);
      
      if (Math.abs(calculatedTotal - data.total_amount) > 0.01) {
        console.warn(`Order total inconsistency detected: Stored=${data.total_amount}, Calculated=${calculatedTotal}`);
        console.log(`Order total corrected from ${data.total_amount} to ${calculatedTotal}`);
        data.total_amount = calculatedTotal;
      }
      
      // Convert from 'profiles' (supabase nested response format) to 'profile' if needed
      if (data.profiles && !data.profile) {
        data.profile = data.profiles;
      }
      
      // Ensure delivery_address is set - default to address if no specific delivery address
      if (!data.delivery_address && data.address) {
        data.delivery_address = data.address;
      }
      
      // Parse the address fields
      if (data.profile && data.profile.address) {
        const parsedAddress = parseAddressData(data.profile.address);
        data.profile.address = parsedAddress;
      }
      
      // Parse guest details address too, if present
      if (data.guest_details) {
        let guestDetails = data.guest_details;
        
        // If guest_details is a string, try to parse it
        if (typeof guestDetails === 'string') {
          try {
            guestDetails = JSON.parse(guestDetails);
          } catch (e) {
            console.error('Error parsing guest details:', e);
          }
        }
        
        if (typeof guestDetails === 'object' && guestDetails.address) {
          guestDetails.address = parseAddressData(guestDetails.address).street;
          data.guest_details = guestDetails;
        }
      }
      
      setOrder(data);
      setOriginalStatus(data.status);
      
      // Also fetch available products
      const products = await fetchProducts();
      setAvailableProducts(products || []);
    } catch (error: any) {
      console.error('Error fetching order:', error);
      
      if (retryCount < 3) {
        console.log(`Retrying fetch (${retryCount + 1}/3)...`);
        setTimeout(() => fetchOrder(id, retryCount + 1, forceRefresh), 1000);
        return;
      }
      
      toast.error(t('admin.failedToLoadOrder'));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available products for add dialog
  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, 
          name, 
          price, 
          description, 
          created_at, 
          updated_at,
          producer:producers (
            id,
            company_name
          )
        `)
        .order('name', { ascending: true });
        
      if (error) throw error;
      return data as Product[];
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  };

  // Initialize data
  useEffect(() => {
    if (orderId) {
      fetchOrder(orderId);
    }
  }, [orderId]);

  // Add product to order
  const handleAddProduct = async () => {
    if (!order || !selectedProduct) return;
    
    try {
      const productToAdd = availableProducts.find(p => p.id === selectedProduct);
      if (!productToAdd) return;
      
      // First update the UI optimistically
      const newItem: OrderItem = {
        id: 'temp-' + Date.now(),
        order_id: order.id,
        product_id: productToAdd.id,
        quantity: itemQuantity,
        product: productToAdd,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setOrder({
        ...order,
        order_items: [...order.order_items, newItem]
      });
      
      // Clear selection
      setSelectedProduct('');
      setItemQuantity(1);
      
      // Now update the database
      const updatedItems = order.order_items.map(item => ({
        id: item.id.startsWith('temp-') ? undefined : item.id,
        product_id: item.product_id,
        quantity: item.quantity
      }));
      
      updatedItems.push({
        id: undefined,
        product_id: productToAdd.id,
        quantity: itemQuantity
      });
      
      const result = await updateOrder(
        order.id,
        null, // Don't change status
        updatedItems,
        null, // Don't update profile
        'admin@example.com'
      );
      
      if (result) {
        setOrder(result);
      }
    } catch (error: any) {
      console.error('Error adding product:', error);
      toast.error(`${t('admin.failedToAddProduct')}: ${error.message}`);
      
      // Refresh order to revert optimistic update
      fetchOrder(order.id);
    }
  };

  // Remove item from order
  const handleRemoveItem = async (itemId: string) => {
    if (!order) return;
    
    console.log(`Removing item ${itemId} from order ${order.id}`);
    
    try {
      // Optimistic UI update
      setOrder({
        ...order,
        order_items: order.order_items.filter(item => item.id !== itemId)
      });
      
      // Remove from database
      await removeOrderItem(itemId, 'admin@example.com');
      
      console.log(`Item ${itemId} successfully deleted from database`);
      
      // Refresh order to get updated totals
      fetchOrder(order.id);
    } catch (error: any) {
      console.error('Error removing item:', error);
      toast.error(`${t('admin.failedToRemoveItem')}: ${error.message}`);
      
      // Refresh order to revert optimistic update
      fetchOrder(order.id);
    }
  };

  // Update item quantity
  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (!order) return;
    
    setOrder({
      ...order,
      order_items: order.order_items.map(item => 
        item.id === itemId 
          ? { ...item, quantity: newQuantity } 
          : item
      )
    });
  };

  // Update item price
  const handlePriceChange = async (itemId: string, newPrice: number) => {
    if (!order) return;
    
    try {
      // Add validation to ensure newPrice is a valid number
      if (isNaN(newPrice) || newPrice < 0) {
        console.error(`Invalid price value: ${newPrice}`);
        toast.error('Invalid price value');
        return;
      }
      
      console.log(`Updating price for item ${itemId} to ${newPrice}`);
      
      // Update UI optimistically
      setOrder({
        ...order,
        order_items: order.order_items.map(item => 
          item.id === itemId 
            ? { ...item, price_at_time: newPrice } 
            : item
        )
      });
      
      // Direct database update with detailed logging
      console.log(`Sending direct database update for item ${itemId}`);
      const { data, error } = await supabase
        .from('order_items')
        .update({ price_at_time: newPrice })
        .eq('id', itemId)
        .select();
        
      if (error) {
        console.error('Database error updating price:', error);
        toast.error('Failed to update price');
        // Revert optimistic update
        fetchOrder(order.id);
        return;
      } else {
        console.log('Price updated successfully in database:', data);
        toast.success('Price updated');
        
        // Update the order total directly
        const calculatedTotal = order.order_items.reduce((total, item) => {
          const itemPrice = item.id === itemId ? newPrice : (item.price_at_time || item.product.price);
          return total + (item.quantity * itemPrice);
        }, 0);
        
        console.log(`Updating order total to ${calculatedTotal}`);
        
        const { error: totalError } = await supabase
          .from('orders')
          .update({ total_amount: calculatedTotal })
          .eq('id', order.id);
          
        if (totalError) {
          console.error('Error updating order total:', totalError);
          toast.error('Updated item price but failed to update order total');
        } else {
          console.log('Order total updated successfully');
        }
      }
    } catch (error) {
      console.error('Error in price update:', error);
      toast.error('An unexpected error occurred');
      // Revert optimistic update
      fetchOrder(order.id);
    }
  };

  // Save all changes
  const handleSave = async () => {
    if (!order) return;
    
    setIsSaving(true);
    
    try {
      console.log(`Saving order ${order.id}`);
      
      // Special handling for 'packed' status - use our utility function which creates
      // the packed status character by character to avoid encoding issues
      if (order.status === 'packed') {
        console.log('Using special utility for packed status - character by character approach');
        
        // First update everything except status
        const itemsToUpdate = order.order_items.map(item => ({
          id: item.id.startsWith('temp-') ? undefined : item.id,
          product_id: item.product_id,
          quantity: item.quantity
        }));
        
        // Prepare delivery information
        const deliveryInfo = {
          delivery_date: order.delivery_date,
          delivery_time: order.delivery_time,
          delivery_notes: order.delivery_notes,
          address: order.delivery_address || order.address,
          guest_email: order.guest_email,
          guest_details: order.guest_details
        };
        
        // Update order without status
        await updateOrder(
          order.id,
          null, // Skip status update
          itemsToUpdate,
          order.profile,
          'admin@example.com',
          deliveryInfo
        );
        
        // Now update just the status with our special function
        const result = await fixOrderPackedStatus(order.id);
        
        if (!result.success) {
          throw new Error('Failed to update status to packed: ' + 
            (result.error ? (typeof result.error === 'string' ? result.error : JSON.stringify(result.error)) : 'Unknown error'));
        }
        
        console.log('Status updated successfully with utility function');
        
        toast.success(t('admin.orderSavedSuccessfully'));
        
        // Refresh the order to get the latest data
        fetchOrder(order.id, 0, true);
        
        if (onSave) {
          onSave(order.id);
        }
        
        return;
      }
      
      // Use hardcoded string for "packed" status to avoid any encoding issues
      const statusToSave = order.status;
      
      // Debug: Show exact status value being sent
      console.log('Final status to save:', statusToSave);
      console.log('Status length:', statusToSave?.length);
      console.log('Status character codes:', [...(statusToSave || '')].map(c => c.charCodeAt(0)));
      
      // Prepare items for update
      const itemsToUpdate = order.order_items.map(item => ({
        id: item.id.startsWith('temp-') ? undefined : item.id,
        product_id: item.product_id,
        quantity: item.quantity
      }));
      
      // Prepare delivery information
      const deliveryInfo = {
        delivery_date: order.delivery_date,
        delivery_time: order.delivery_time,
        delivery_notes: order.delivery_notes,
        address: order.delivery_address || order.address,
        guest_email: order.guest_email,
        guest_details: order.guest_details
      };
      
      // Normal flow - all statuses
      const result = await updateOrder(
        order.id,
        statusToSave,
        itemsToUpdate,
        order.profile,
        'admin@example.com',
        deliveryInfo
      );
      
      toast.success(t('admin.orderSavedSuccessfully'));
      
      // Refresh the order to get the latest data with the correct status
      fetchOrder(order.id, 0, true);
      
      if (onSave) {
        onSave(order.id);
      }

      // If status changed to confirmed, send confirmation email
      if (order.status === 'confirmed') {
        try {
          await sendOrderStatusEmail(order);
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Don't throw here - we still want to save the status change even if email fails
        }
      }
    } catch (error: any) {
      console.error('Error saving order:', error);
      toast.error(`${t('admin.failedToSaveOrder')}: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Update status and send email notification
  const handleStatusChange = async (newStatus: string) => {
    if (!order) return;
    
    try {
      setIsSaving(true);
      
      // Update order status
      const updatedOrder = await updateOrder(
        order.id,
        newStatus,
        null, // Don't change items
        null, // Don't update profile
        'admin@example.com'
      );
      
      if (updatedOrder) {
        setOrder(updatedOrder);
        
        // Send email notification using new unified service
        const result = await sendOrderStatusEmail(updatedOrder);
        if (!result.success) {
          console.error('Failed to send email notification:', result.error);
          toast.error(t('admin.failedToSendEmail'));
        }
      }
      
      toast.success(t('admin.orderUpdated'));
    } catch (error: any) {
      console.error('Error updating order status:', error);
      toast.error(`${t('admin.failedToUpdateOrder')}: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // UI rendering
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      {isLoading ? (
        <div className="py-20 flex justify-center items-center">
          <Loader2 className="h-8 w-8 text-gray-500 animate-spin" />
        </div>
      ) : !order ? (
        <div className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Beställning hittades inte</h3>
          <p className="mt-2 text-sm text-gray-500">
            Beställningen du försöker redigera finns inte eller har raderats.
          </p>
        </div>
      ) : (
        <div className="px-4 py-5 sm:p-6">
          {/* Add close button at the top */}
          {onCancel && (
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
              >
                <X size={16} className="mr-1" />
                Tillbaka till orderlistan
              </button>
            </div>
          )}
          
          {/* Customer information section - prominently displayed at the top */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div 
              className="flex justify-between items-center cursor-pointer" 
              onClick={() => setShowCustomerDetails(!showCustomerDetails)}
            >
              <div className="flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-500" />
                <h3 className="text-lg font-medium text-gray-900">
                  Kunduppgifter
                </h3>
              </div>
              <button className="text-gray-500 hover:text-gray-700">
                {showCustomerDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            </div>
            
            {showCustomerDetails && (
              <>
                {/* Debug info - remove in production */}
                {order.guest_email && !order.profile && (
                  <div className="p-2 bg-yellow-50 rounded mb-3 mt-3 text-xs">
                    <p><strong>Gästkund:</strong> {order.guest_email}</p>
                    {order.guest_details && (
                      <p><strong>Detaljer:</strong> {typeof order.guest_details === 'string' ? order.guest_details : JSON.stringify(order.guest_details)}</p>
                    )}
                  </div>
                )}
                
                {order.profile ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Namn</p>
                      <div className="mt-1 flex space-x-2">
                        <input
                          type="text"
                          value={order.profile.first_name || ''}
                          onChange={(e) => updateProfileField('first_name', e.target.value)}
                          placeholder="Förnamn"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                        <input
                          type="text"
                          value={order.profile.last_name || ''}
                          onChange={(e) => updateProfileField('last_name', e.target.value)}
                          placeholder="Efternamn"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">E-post</p>
                      <input
                        type="email"
                        value={order.profile.email || ''}
                        onChange={(e) => updateProfileField('email', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Telefon</p>
                      <input
                        type="tel"
                        value={order.profile.phone || ''}
                        onChange={(e) => updateProfileField('phone', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Gatuadress</p>
                      <input
                        type="text"
                        value={typeof order.profile.address === 'object' 
                          ? order.profile.address?.street || ''
                          : order.profile.address || ''}
                        onChange={(e) => updateProfileAddress(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Postnummer</p>
                      <input
                        type="text"
                        value={typeof order.profile.address === 'object' 
                          ? order.profile.address?.postal_code || ''
                          : ''}
                        onChange={(e) => {
                          if (!order.profile) return;
                          let newAddress = typeof order.profile.address === 'object'
                            ? { ...order.profile.address, postal_code: e.target.value }
                            : { street: order.profile.address || '', postal_code: e.target.value };
                          setOrder({
                            ...order,
                            profile: {
                              ...order.profile,
                              address: newAddress
                            }
                          });
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Ort</p>
                      <input
                        type="text"
                        value={typeof order.profile.address === 'object' 
                          ? order.profile.address?.city || ''
                          : ''}
                        onChange={(e) => {
                          if (!order.profile) return;
                          let newAddress = typeof order.profile.address === 'object'
                            ? { ...order.profile.address, city: e.target.value }
                            : { street: order.profile.address || '', city: e.target.value };
                          setOrder({
                            ...order,
                            profile: {
                              ...order.profile,
                              address: newAddress
                            }
                          });
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    {/* Guest customer information editing */}
                    {(() => {
                      // Parse guest details if it's a string
                      let guestDetails: any = order.guest_details;
                      if (typeof guestDetails === 'string') {
                        try {
                          guestDetails = JSON.parse(guestDetails);
                        } catch (e) {
                          console.error('Error parsing guest details:', e);
                          guestDetails = {};
                        }
                      }
                      
                      // Function to update guest details
                      const updateGuestDetails = (field: string, value: string) => {
                        const updatedDetails = { ...(typeof guestDetails === 'object' ? guestDetails : {}) };
                        updatedDetails[field] = value;
                        
                        setOrder({
                          ...order,
                          guest_details: updatedDetails
                        });
                      };
                      
                      return (
                        <>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Namn</p>
                            <div className="mt-1 flex space-x-2">
                              <input
                                type="text"
                                value={guestDetails && typeof guestDetails === 'object' ? guestDetails.firstName || '' : ''}
                                onChange={(e) => updateGuestDetails('firstName', e.target.value)}
                                placeholder="Förnamn"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                              <input
                                type="text"
                                value={guestDetails && typeof guestDetails === 'object' ? guestDetails.lastName || '' : ''}
                                onChange={(e) => updateGuestDetails('lastName', e.target.value)}
                                placeholder="Efternamn"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">E-post <span className="text-red-500">*</span></p>
                            <input
                              type="email"
                              value={order.guest_email || ''}
                              onChange={(e) => setOrder({
                                ...order,
                                guest_email: e.target.value
                              })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              required
                            />
                            {!order.guest_email && (
                              <p className="mt-1 text-sm text-red-600">E-postadress krävs för att skicka orderstatusar</p>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Telefon</p>
                            <input
                              type="tel"
                              value={guestDetails && typeof guestDetails === 'object' ? guestDetails.phone || '' : ''}
                              onChange={(e) => updateGuestDetails('phone', e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Gatuadress</p>
                            <input
                              type="text"
                              value={guestDetails && typeof guestDetails === 'object' ? guestDetails.address || '' : ''}
                              onChange={(e) => updateGuestDetails('address', e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Postnummer</p>
                            <input
                              type="text"
                              value={guestDetails && typeof guestDetails === 'object' ? guestDetails.postal_code || '' : ''}
                              onChange={(e) => updateGuestDetails('postal_code', e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500">Ort</p>
                            <input
                              type="text"
                              value={guestDetails && typeof guestDetails === 'object' ? guestDetails.city || '' : ''}
                              onChange={(e) => updateGuestDetails('city', e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                          </div>

                          {/* Add email to guest details to ensure it's stored in both places */}
                          {order.guest_email && (
                            <input
                              type="hidden"
                              value={order.guest_email}
                              onChange={() => updateGuestDetails('email', order.guest_email || '')}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Delivery Information */}
          <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-100">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.7-.29l4-4a1 1 0 00.3-.71v-1a1 1 0 00-1-1H3z" />
              </svg>
              Leveransinformation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Leveransdatum</p>
                <input
                  type="date"
                  value={order.delivery_date || ''}
                  onChange={(e) => setOrder({
                    ...order,
                    delivery_date: e.target.value
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Leveranstid</p>
                <input
                  type="time"
                  value={order.delivery_time || ''}
                  onChange={(e) => setOrder({
                    ...order,
                    delivery_time: e.target.value
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Leveransanteckningar</p>
                <textarea
                  value={order.delivery_notes || ''}
                  onChange={(e) => setOrder({
                    ...order,
                    delivery_notes: e.target.value
                  })}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="Särskilda instruktioner för leverans..."
                />
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Leveransadress</p>
                <input
                  type="text"
                  value={order.delivery_address || ''}
                  onChange={(e) => setOrder({
                    ...order,
                    delivery_address: e.target.value
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="Leveransadress om annan än kundadress"
                />
              </div>
            </div>
          </div>
          
          {/* Order header */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Ordernummer
              </label>
              <div className="text-xl font-bold">
                {order.display_id ? `#${order.display_id}` : 'N/A'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                System-ID
              </label>
              <div className="text-xs font-mono bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto">
                {order.id}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Datum
              </label>
              <div className="text-sm">
                <SwedishDateDisplay date={order.created_at} format="datetime" />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Status
              </label>
              <select
                value={order.status}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  console.log(`Changing status from "${order.status}" to "${newStatus}"`);
                  // Display exact character codes for debugging
                  console.log('Status character codes:', [...newStatus].map(c => c.charCodeAt(0)));
                  handleStatusChange(newStatus);
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              >
                {/* Use hardcoded values with no unexpected spaces or characters */}
                <option value="pending">Väntar</option>
                <option value="confirmed">Bekräftad</option>
                <option value="preparing">Förbereds</option>
                <option value="out_for_delivery">Under leverans</option>
                <option value="delivered">Levererad</option>
                <option value="cancelled">Avbruten</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Summa
              </label>
              <div className="text-lg font-semibold text-gray-900">
                {calculateOrderTotal().toFixed(2)} kr
              </div>
              {/* Display VAT information */}
              {Object.entries(calculateVatAmounts()).map(([rate, values]) => 
                values.vatAmount > 0 ? (
                  <div key={`vat-summary-${rate}`} className="text-sm text-gray-600 mt-1">
                    Summa {rate}%: {values.totalAmount.toFixed(2)} kr ({values.baseAmount.toFixed(2)} kr + {values.vatAmount.toFixed(2)} kr moms)
                  </div>
                ) : null
              )}
            </div>
          </div>
          
          {/* Order items */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Produkter
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produkt
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Producent
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pris
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Antal
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Summa
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Ta bort</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {order.order_items
                    .sort((a, b) => {
                      // First sort by producer company name
                      const producerNameA = a.product.producer?.company_name || '';
                      const producerNameB = b.product.producer?.company_name || '';
                      
                      if (producerNameA !== producerNameB) {
                        return producerNameA.localeCompare(producerNameB);
                      }
                      
                      // Then sort by product name
                      return a.product.name.localeCompare(b.product.name);
                    })
                    .map((item) => (
                    <tr key={item.id} className={`${item.status === 'picked' ? 'bg-green-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          {loadingItemId === item.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : item.status === 'picked' ? (
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                          ) : null}
                          {item.product.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.product.producer?.company_name || 'Okänd producent'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price_at_time || item.product.price}
                          onChange={(e) => handlePriceChange(item.id, parseFloat(e.target.value) || 0)}
                          className="w-24 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm text-right"
                        />
                        <span className="ml-1">kr</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                          className="w-16 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm text-right"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {(item.quantity * (item.price_at_time || item.product.price)).toFixed(2)} kr
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Add new product row */}
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={selectedProduct}
                        onChange={(e) => setSelectedProduct(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      >
                        <option value="">Välj produkt...</option>
                        {availableProducts.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      -
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {selectedProduct 
                        ? `${availableProducts.find(p => p.id === selectedProduct)?.price.toFixed(2) || 0} kr` 
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <input
                        type="number"
                        min="1"
                        value={itemQuantity}
                        onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                        className="w-16 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm text-right"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {selectedProduct 
                        ? `${(itemQuantity * (availableProducts.find(p => p.id === selectedProduct)?.price || 0)).toFixed(2)} kr` 
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        type="button"
                        onClick={handleAddProduct}
                        disabled={!selectedProduct || isSaving}
                        className={`${
                          !selectedProduct 
                            ? 'bg-gray-300 cursor-not-allowed' 
                            : 'bg-green-600 hover:bg-green-700'
                        } inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
                      >
                        Lägg till
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  {/* VAT information fields */}
                  {Object.entries(calculateVatAmounts()).map(([rate, values]) => 
                    values.vatAmount > 0 ? (
                      <tr key={`vat-${rate}`}>
                        <th 
                          colSpan={3}
                          className="px-6 py-2 text-right text-sm font-medium text-gray-600"
                        >
                          Summa {rate}%: {values.totalAmount.toFixed(2)} kr ({values.baseAmount.toFixed(2)} kr + {values.vatAmount.toFixed(2)} kr moms)
                        </th>
                        <th className="px-6 py-2 text-right text-sm font-medium text-gray-600">
                          {values.totalAmount.toFixed(2)} kr
                        </th>
                        <th></th>
                      </tr>
                    ) : null
                  )}
                  <tr>
                    <th 
                      colSpan={3}
                      className="px-6 py-3 text-right text-sm font-medium text-gray-900"
                    >
                      Summa:
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                      {calculateOrderTotal().toFixed(2)} kr
                    </th>
                    <th></th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          
          {/* Order history table */}
          {order.order_history && order.order_history.length > 0 && (
            <OrderHistoryTable historyItems={order.order_history} />
          )}
          
          {/* Scanner toggle button - place it where appropriate in your UI */}
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => setIsScannerActive(!isScannerActive)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {isScannerActive ? (
                <>
                  <CameraOff className="h-5 w-5 mr-2" />
                  Stop Scanner
                </>
              ) : (
                <>
                  <Camera className="h-5 w-5 mr-2" />
                  Start Scanner
                </>
              )}
            </button>
          </div>
          
          {/* Scanner component */}
          {isScannerActive && (
            <div className="mb-4">
              <div className="max-w-md mx-auto bg-gray-100 p-4 rounded-lg">
                <video ref={ref as React.LegacyRef<HTMLVideoElement>} className="w-full" />
                {scanError && (
                  <p className="text-red-600 text-sm mt-2">Error: {scanError}</p>
                )}
                {lastScanResult && (
                  <p className="text-gray-600 text-sm mt-2">Last scan: {lastScanResult}</p>
                )}
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex justify-end space-x-3 mt-8">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <X size={16} className="mr-1" />
                Stäng
              </button>
            )}
            
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                isSaving ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSaving ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderEditor; 